import React, { useEffect, useState, useCallback } from 'react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Search, Filter, X, Loader2 } from 'lucide-react';
import { POSITIONS, PROVINCES } from '../mock';
import PlayerCard from '../components/PlayerCard';
import { playersApi } from '../lib/api';

const PAGE_SIZE = 24;

export default function Discover() {
  const [players, setPlayers] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [name, setName] = useState('');
  const [position, setPosition] = useState('all');
  const [province, setProvince] = useState('all');
  const [availableOnly, setAvailableOnly] = useState(false);

  const buildParams = useCallback((skipVal) => ({
    q: name || undefined,
    position: position !== 'all' ? position : undefined,
    province: province !== 'all' ? province : undefined,
    available_only: availableOnly || undefined,
    skip: skipVal,
    limit: PAGE_SIZE,
  }), [name, position, province, availableOnly]);

  // Fetch first page whenever filters change (debounced)
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await playersApi.list(buildParams(0));
        if (!cancelled) {
          setPlayers(data.items || []);
          setTotal(data.total || 0);
          setSkip(data.items ? data.items.length : 0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [buildParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await playersApi.list(buildParams(skip));
      setPlayers((prev) => [...prev, ...(data.items || [])]);
      setTotal(data.total || 0);
      setSkip((s) => s + (data.items ? data.items.length : 0));
    } finally {
      setLoadingMore(false);
    }
  };

  const clearFilters = () => { setName(''); setPosition('all'); setProvince('all'); setAvailableOnly(false); };
  const active = name || position !== 'all' || province !== 'all' || availableOnly;
  const hasMore = players.length < total;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#eaf7ef] via-[#fff8e6] to-[#ffe1d6] border border-white p-6 md:p-8 mb-8 shadow-sm">
        <div className="mft-blob mft-blob--green" style={{ width: 200, height: 200, top: -60, right: '20%', opacity: 0.4 }} />
        <div className="mft-blob mft-blob--gold" style={{ width: 180, height: 180, bottom: -60, right: -40, opacity: 0.4 }} />
        <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="inline-block bg-white/80 backdrop-blur border border-gray-200 text-[color:var(--mft-green)] text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
              Free-agent hub
            </span>
            <h1 className="mt-3 text-3xl md:text-4xl font-black text-gray-900">Discover Talent</h1>
            <p className="text-gray-600 mt-1">Search South African football free-agents by name, position or province.</p>
          </div>
          <div className="text-sm font-bold text-gray-900 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-200 inline-flex items-center gap-2 self-start md:self-end">
            <span className="mft-dot" />
            {loading ? 'Searching...' : `${total} player${total !== 1 ? 's' : ''} found`}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-5 shadow-sm mb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-5 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Search by name or nickname..." className="pl-9 mft-input h-11" />
          </div>
          <div className="md:col-span-3">
            <Select value={position} onValueChange={setPosition}>
              <SelectTrigger className="h-11 mft-input"><SelectValue placeholder="Position" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All positions</SelectItem>
                {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Select value={province} onValueChange={setProvince}>
              <SelectTrigger className="h-11 mft-input"><SelectValue placeholder="Province" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All provinces</SelectItem>
                {PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1 flex">
            <Button variant="outline" className="w-full h-11" disabled={!active} onClick={clearFilters}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-4 text-sm">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="accent-[color:var(--mft-green)] w-4 h-4" checked={availableOnly} onChange={(e) => setAvailableOnly(e.target.checked)} />
            <span className="text-gray-700">Only players available for offers</span>
          </label>
          {active && (<span className="inline-flex items-center gap-1 text-xs text-gray-500"><Filter className="w-3.5 h-3.5" /> Filters active</span>)}
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin"/></div>
      ) : players.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl py-16 text-center">
          <p className="text-gray-500">No players match your filters.</p>
          <Button variant="outline" className="mt-4" onClick={clearFilters}>Clear filters</Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {players.map((p) => <PlayerCard key={p.id} player={p} />)}
          </div>

          <div className="mt-8 flex flex-col items-center gap-2">
            <div className="text-xs text-gray-500">Showing {players.length} of {total}</div>
            {hasMore && (
              <Button onClick={loadMore} disabled={loadingMore} variant="outline" className="min-w-[180px]">
                {loadingMore ? <Loader2 className="w-4 h-4 animate-spin"/> : 'Load more players'}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
